import React from 'react';
import { extractVideoId } from '../utils/youtubeUtils';
import '../styles/global.css';
import { useConfig } from '../config/ConfigContext';
import VideoPlayer from './Video/VideoPlayer';

// Helper function to format seconds as MM:SS
const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * FullVideo component for embedding the complete lecture video
 * Uses VideoPlayer with clip times when available, otherwise falls back to iframe
 * @param {Object} props - Component props
 * @param {string} props.url - URL of the video to display
 * @param {number} props.clipStartTime - Start time in seconds (optional)
 * @param {number} props.clipEndTime - End time in seconds (optional)
 * @returns {JSX.Element} FullVideo component
 */
const FullVideo = ({ url, clipStartTime, clipEndTime }) => {
  const { theme } = useConfig();
  const videoUrl = url || '';
  const videoId = extractVideoId(videoUrl);

  // Check if we have clip bounds defined
  const hasClipBounds = clipStartTime != null || clipEndTime != null;
  
  if (!videoId) {
    return (
      <div className="video-placeholder" style={{
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.colors.lightGray,
        borderRadius: theme.borderRadius.medium,
        margin: '20px auto',
        maxWidth: '800px',
        height: '400px',
        textAlign: 'center'
      }}>
        <h2 style={{ color: theme.colors.primary, marginBottom: '20px' }}>Full Lecture Video</h2>
        <p>No full lecture video is available for this lecture yet. Check back later for updates.</p>
      </div>
    );
  }
  
  // Use VideoPlayer with clip times for constrained playback
  if (hasClipBounds) {
    const videoData = {
      youtube_video_id: videoId,
      clipStartTime: clipStartTime || 0,
      clipEndTime: clipEndTime,
      fullVideoUrl: videoUrl
    };

    return (
      <div className="full-video-container" style={{
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        <h2 style={{
          color: theme.colors.primary,
          marginBottom: '20px',
          textAlign: 'center',
          fontFamily: theme.fonts.secondary
        }}>
          Full Lecture Video
        </h2>

        <div className="video-wrapper" style={{
          width: '100%',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <VideoPlayer video={videoData} autoplay={false} />
        </div>

        {/* Info about clip playback */}
        <p style={{
          marginTop: '12px',
          fontSize: '0.9em',
          color: '#666',
          textAlign: 'center'
        }}>
          Playing lecture segment ({formatTime(clipStartTime || 0)} - {clipEndTime ? formatTime(clipEndTime) : 'end'})
        </p>

        {/* Watch Full Video on YouTube link */}
        <p style={{
          marginTop: '8px',
          fontSize: '0.85em',
          fontStyle: 'italic',
          color: '#666',
          textAlign: 'center'
        }}>
          Watch Full Lecture on YouTube:{' '}
          <a
            href={videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: theme.colors.primary,
              textDecoration: 'underline'
            }}
          >
            {videoUrl}
          </a>
        </p>
      </div>
    );
  }

  // Fallback to basic iframe when no clip times are defined
  return (
    <div className="full-video-container" style={{
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      <h2 style={{
        color: theme.colors.primary,
        marginBottom: '20px',
        textAlign: 'center',
        fontFamily: theme.fonts.secondary
      }}>
        Full Lecture Video
      </h2>

      <div className="video-wrapper" style={{
        width: '100%',
        maxWidth: '800px',
        margin: '0 auto'
      }}>
        <div className="video-container" style={{
          position: 'relative',
          paddingBottom: '56.25%', /* 16:9 aspect ratio */
          height: '0',
          overflow: 'hidden',
          maxWidth: '100%',
          backgroundColor: '#000',
          borderRadius: theme.borderRadius.small
        }}>
          <iframe
            style={{
              position: 'absolute',
              top: '0',
              left: '0',
              width: '100%',
              height: '100%',
              border: 'none'
            }}
            src={`https://www.youtube.com/embed/${videoId}?rel=0&showinfo=0&modestbranding=1`}
            title="Full Lecture Video"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
};

export default FullVideo;
